import React, { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { push } from 'connected-react-router';
import PropTypes from 'prop-types';
import { flattenDeep } from 'lodash';
import { downloadCSVExport } from '@actions/data-table-actions';
import { openDashboardDialog } from '@actions/dashboard-actions';
import { useCanAccessType, useTypeIsReadOnly } from '@components/config/permissions/hooks';
import { getDataTypeConfig } from '@components/config/selectors';
import { getDataTypeFilterParams } from '@components/filters/selectors';
import { getDashboardMenuConfig } from '@constants/config';
import * as dialog from '@constants/dialogs';
import Button from '@material-ui/core/Button';
import AddButton from '@shared/add-button';
import ExportButton from '@shared/export-button';
import './header.scss';

const getAddTooltip = (dataType, subType) => `Add ${subType ? subType : dataType}`;

const Header = ({ dataType, subType }) => {
  const dispatch = useDispatch();
  const menuConfig = getDashboardMenuConfig();
  const menuItems = Array.isArray(menuConfig) ? flattenDeep(menuConfig.map(section => section.items)) : [];
  const label = menuItems.find(item => item.type === dataType)?.label || dataType;

  // Get type config for top-bar actions (used by People pages)
  const typeConfig = useSelector(state => getDataTypeConfig(state, dataType, subType));
  const { ['top-bar']: { link, permission } = {} } = typeConfig || {};
  const readOnly = useTypeIsReadOnly(dataType, subType);
  const hasLinkAccess = useCanAccessType(null, dataType, subType, permission);

  // Get filter params for export
  const filterParams = useSelector(state => getDataTypeFilterParams(state, dataType, subType));

  const openDialog = useCallback(
    () => dispatch(openDashboardDialog(dialog.NAME_DESCRIPTION, { dataType, portal: 'admin' })),
    [dataType, dispatch]
  );

  const onAdd = useCallback(() => {
    if (link) {
      dispatch(push(link.to));
    }
  }, [dispatch, link]);

  const onExport = useCallback(() => {
    dispatch(downloadCSVExport(dataType, subType, filterParams));
  }, [dispatch, dataType, subType, filterParams]);

  // Determine which action buttons to show
  const showAddButton = link && hasLinkAccess && !readOnly;
  const showCreateButton = !link; // Admin portal style (workflow, etc.)

  return (
    <div data-testid="header" styleName="header-container">
      <div styleName="title">
        {label}
      </div>
      <div styleName="action">
        {showAddButton && (
          <AddButton onClick={onAdd} tooltip={getAddTooltip(dataType, subType)} />
        )}
        {showAddButton && (
          <ExportButton onClick={onExport} />
        )}
        {showCreateButton && (
          <Button
            color="primary"
            onClick={openDialog}
            variant="contained"
          >
            CREATE {label}
          </Button>
        )}
      </div>
    </div>
  );
};

Header.propTypes = {
  dataType: PropTypes.string,
  subType: PropTypes.string
};

export default Header;
